import styled from "styled-components"

import { Banner, spacing, Text } from "@minuthq/meatball-ui-react"
import { addDays } from "date-fns"

import { useFetchParadiseUserEmail } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { IParadiseUserEmailResponse } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { MDialog } from "src/ui/Dialog/MDialog"
import { MBadge } from "src/ui/MBadge/MBadge"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { formatDate, formatUtcDate } from "src/utils/l10n"

export const POSTMARK_RETENTION_DAYS = 45

export function EmailDialog({
  userId,
  open,
  onClose,
  emailId,
}: {
  userId: string
  open: boolean
  onClose: () => void
  emailId: string | null
}) {
  const fetchSelectedEmail = useFetchParadiseUserEmail({
    userId,
    emailId: emailId ?? "",
    options: {
      enabled: !!emailId,
      staleTime: 5 * 60 * 1000,
    },
  })

  const email = fetchSelectedEmail.data

  const isLoading = fetchSelectedEmail.isLoading
  return (
    <MDialog
      width="large"
      open={open}
      onClose={onClose}
      onConfirm={onClose}
      hideClose
      confirmLabel="Close"
      title={<DialogHeader email={email} isLoading={false} />}
    >
      {!isLoading && (
        <>
          <div>
            <iframe
              sandbox=""
              srcDoc={email?.body ?? ""}
              style={{
                width: "100%",
                height: "100%",
                minHeight: "500px",
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                marginBottom: spacing.M,
              }}
            ></iframe>
          </div>
          {email && (
            <Banner type="info" fullWidth>
              This email will be stored until{" "}
              {formatDate({
                date: addDays(
                  new Date(email.sent_at),
                  POSTMARK_RETENTION_DAYS
                ).toISOString(),
              })}
            </Banner>
          )}
        </>
      )}
    </MDialog>
  )
}

function DialogHeader({
  email,
  isLoading,
}: {
  email: IParadiseUserEmailResponse | undefined
  isLoading: boolean
}) {
  const labels = ["Subject", "Sent at", "From", "Status"]

  if (isLoading) {
    return (
      <DialogHeaderWrapper>
        {labels.map((label) => (
          <>
            <DialogHeaderLabel key={label} variant="bodyS">
              {label}:
            </DialogHeaderLabel>
            <MSkeleton variant="text" width="100%" height={20} />
          </>
        ))}
      </DialogHeaderWrapper>
    )
  }

  return (
    <DialogHeaderWrapper>
      <DialogHeaderLabel variant="bodyS">Subject:</DialogHeaderLabel>
      <Text variant="subtitle">{email?.subject}</Text>
      <DialogHeaderLabel variant="bodyS">Sent at:</DialogHeaderLabel>
      <Text variant="subtitleS">
        {formatUtcDate({ date: email?.sent_at ?? "" })}
      </Text>
      <DialogHeaderLabel variant="bodyS">Delivered at:</DialogHeaderLabel>
      <Text variant="subtitleS">
        {email?.delivered_at
          ? formatUtcDate({ date: email?.delivered_at ?? "" })
          : "Not delivered"}
      </Text>
      <DialogHeaderLabel variant="bodyS">From:</DialogHeaderLabel>
      <Text variant="subtitleS">{email?.from}</Text>
      <DialogHeaderLabel variant="bodyS">Status:</DialogHeaderLabel>
      {email?.opened ? (
        <BadgeContainer>
          <MBadge color="good" size="small">
            Opened ({formatUtcDate({ date: email?.opened_at ?? "" })})
          </MBadge>
        </BadgeContainer>
      ) : (
        <BadgeContainer>
          <MBadge color="warning" size="small">
            Not opened
          </MBadge>
        </BadgeContainer>
      )}
    </DialogHeaderWrapper>
  )
}

const DialogHeaderWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.XS};
  align-items: center;
  padding-left: ${spacing.M};
`

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
`

const DialogHeaderLabel = styled(Text)`
  justify-self: flex-end;
`
